import { z } from "zod";

const numberFieldSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().positive().optional(),
  defaultValue: z.number().optional(),
  unit: z.string().optional(),
});

const baseQuestionSchema = z.object({
  id: z.string().min(1),
  prompt: z.string().min(1),
  explanation: z.string().optional(),
  tags: z.array(z.string()).default([]),
  competencyIds: z.array(z.string()).default([]),
  points: z.number().int().positive().default(1),
});

const multipleChoiceQuestionSchema = baseQuestionSchema.extend({
  type: z.literal("multiple-choice"),
  choices: z
    .array(
      z.object({
        id: z.string().min(1),
        text: z.string().min(1),
        correct: z.boolean().default(false),
      }),
    )
    .min(2)
    .max(6),
  allowMultiple: z.boolean().default(false),
});

const calculationQuestionSchema = baseQuestionSchema.extend({
  type: z.literal("calculation"),
  variables: z
    .array(
      z.object({
        key: z.string().min(1),
        min: z.number(),
        max: z.number(),
        step: z.number().positive().default(1),
        decimals: z.number().int().min(0).max(6).default(0),
        unit: z.string().optional(),
      }),
    )
    .default([]),
  answerExpression: z.string().min(1),
  tolerance: z.number().min(0).default(0),
  unit: z.string().optional(),
});

export const reflectionQuestionSchema = z.discriminatedUnion("type", [
  multipleChoiceQuestionSchema,
  calculationQuestionSchema,
]);

export const reflectionDefinitionSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("text"),
    prompt: z.string().min(1),
    minLength: z.number().int().min(0).default(10),
    maxLength: z.number().int().positive().optional(),
    timeLimitSeconds: z.number().int().positive().optional(),
  }),
  z.object({
    mode: z.literal("quiz"),
    title: z.string().min(1),
    instructions: z.string().optional(),
    timeLimitSeconds: z.number().int().positive().optional(),
    drawCount: z.number().int().positive().optional(),
    shuffleQuestions: z.boolean().default(true),
    shuffleChoices: z.boolean().default(true),
    passingScore: z.number().int().min(0).max(100).default(70),
    questions: z.array(reflectionQuestionSchema).min(1),
  }),
]);

export const puzzlePackageSchema = z.object({
  schemaVersion: z.literal("mathportal.puzzle-package.v1"),
  packageId: z.string().min(1),
  title: z.string().min(1),
  description: z.string().default(""),
  templateId: z.enum([
    "vector-drone",
    "trig-cannon",
    "platformer-jump",
    "coordinate-maze",
    "binary-beacon",
    "vector-cannon",
    "spawn-point-builder",
    "pathfinding-trail",
    "quest-equation",
    "turret-aim",
    "transformation-machine",
    "force-arena",
    "collision-zone",
    "xp-counter-overflow",
    "sat-inspector",
    "coming-soon",
  ]),
  templateVersion: z.number().int().positive().default(1),
  config: z.record(z.unknown()).default({}),
  authoringFields: z.array(numberFieldSchema).default([]),
  questDefaults: z.object({
    storyHook: z.string().min(1),
    learningGoal: z.string().min(1),
    practice: z.string().min(1),
    xp: z.number().int().min(0).default(300),
    gradePoints: z.number().int().min(0).default(10),
    cooldownMinutes: z.number().int().min(0).default(0),
    maxAttempts: z.number().int().positive().nullable().default(null),
  }),
  reflection: reflectionDefinitionSchema,
});

export type ReflectionQuestion = z.infer<typeof reflectionQuestionSchema>;
export type ReflectionDefinition = z.infer<typeof reflectionDefinitionSchema>;
export type PuzzlePackageDefinition = z.infer<typeof puzzlePackageSchema>;
